import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MobileNav } from '../components/MobileNav';
import { Settings, Lock, Phone, Globe, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config';
import { useI18n } from '../hooks/useI18n';
import { setLanguage as persistLanguage } from '../i18n';

const playfairFont = "'Playfair Display', serif";

export const AccountSettings = () => {
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);

  // Password form
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [pwSubmitting, setPwSubmitting] = useState(false);

  // Phone form
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [otpRequested, setOtpRequested] = useState(false);
  const [phoneSubmitting, setPhoneSubmitting] = useState(false);

  useEffect(() => { fetchAccount(); }, []);

  const fetchAccount = async () => {
    try {
      const { data } = await axios.get(`${API}/account/me`, { withCredentials: true });
      setAccount(data);
      setPhone(data.phone || '');
      // Sync server-side language to local if user already saved it
      if (data.language && data.language !== lang) {
        persistLanguage(data.language);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm) { toast.error(t('passwordMismatch')); return; }
    if (pwForm.new_password.length < 6) { toast.error(t('passwordTooShort')); return; }
    setPwSubmitting(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      }, { withCredentials: true });
      toast.success(lang === 'id' ? 'Password berhasil diganti' : 'Password updated');
      setPwForm({ current_password: '', new_password: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error'));
    } finally {
      setPwSubmitting(false);
    }
  };

  const handleRequestOtp = async () => {
    if (!phone.trim()) { toast.error(t('fillAllFields')); return; }
    setPhoneSubmitting(true);
    try {
      await axios.post(`${API}/account/phone/request-otp`, { phone }, { withCredentials: true });
      setOtpRequested(true);
      toast.success(lang === 'id' ? 'Kode OTP dikirim (cek log server untuk demo)' : 'OTP sent (check server logs for demo)');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error'));
    } finally {
      setPhoneSubmitting(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) { toast.error(lang === 'id' ? 'Kode OTP harus 6 digit' : 'OTP must be 6 digits'); return; }
    setPhoneSubmitting(true);
    try {
      await axios.post(`${API}/account/phone/verify-otp`, { phone, otp }, { withCredentials: true });
      toast.success(lang === 'id' ? 'Nomor HP berhasil diverifikasi' : 'Phone verified successfully');
      setOtpRequested(false);
      setOtp('');
      fetchAccount();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error'));
    } finally {
      setPhoneSubmitting(false);
    }
  };

  const handleLanguageChange = async (newLang) => {
    persistLanguage(newLang);
    try {
      await axios.post(`${API}/account/language`, { language: newLang }, { withCredentials: true });
      toast.success(t('languageSaved'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error'));
    }
  };

  const inputStyle = { background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', color: '#F8FAF9' };
  const cardStyle = { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' };
  const goldBtn = { background: '#D4AF37', color: '#080C0A' };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#080C0A' }}>
        <div className="w-12 h-12 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#D4AF37', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-32" style={{ background: '#080C0A', fontFamily: "'Outfit', sans-serif" }}>
      <header className="sticky top-0 z-40 backdrop-blur-2xl" style={{ background: 'rgba(8,12,10,0.9)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="hover:opacity-80" data-testid="back-btn">
            <ArrowLeft className="w-6 h-6" style={{ color: '#F8FAF9' }} />
          </button>
          <h1 className="text-2xl font-light flex items-center gap-3" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>
            <Settings className="w-6 h-6" style={{ color: '#D4AF37' }} /> {t('accountSettingsTitle')}
          </h1>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-8 space-y-8">

        {/* Security: Change Password */}
        <section data-testid="section-security">
          <h2 className="text-xl font-light mb-4 flex items-center gap-2" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>
            <Lock className="w-5 h-5" style={{ color: '#D4AF37' }} /> {t('securitySection')}
          </h2>
          <form onSubmit={handleChangePassword} className="rounded-2xl p-6 backdrop-blur-xl space-y-4" style={cardStyle} data-testid="change-password-form">
            <div>
              <label className="block text-sm mb-2" style={{ color: '#A3B8AC' }}>{t('currentPassword')}</label>
              <input type="password" required value={pwForm.current_password} onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })} className="w-full rounded-lg px-4 py-3 outline-none" style={inputStyle} data-testid="current-pw-input" />
            </div>
            <div>
              <label className="block text-sm mb-2" style={{ color: '#A3B8AC' }}>{t('newPassword')}</label>
              <input type="password" required minLength={6} value={pwForm.new_password} onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} className="w-full rounded-lg px-4 py-3 outline-none" style={inputStyle} data-testid="new-pw-input" />
            </div>
            <div>
              <label className="block text-sm mb-2" style={{ color: '#A3B8AC' }}>{t('confirmPassword')}</label>
              <input type="password" required value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} className="w-full rounded-lg px-4 py-3 outline-none" style={inputStyle} data-testid="confirm-pw-input" />
            </div>
            <motion.button whileTap={{ scale: 0.98 }} type="submit" disabled={pwSubmitting} className="w-full py-3 rounded-full font-semibold disabled:opacity-50" style={goldBtn} data-testid="change-password-submit">
              {pwSubmitting ? t('loading') : t('updatePassword')}
            </motion.button>
          </form>
        </section>

        {/* Phone Verification */}
        <section data-testid="section-phone">
          <h2 className="text-xl font-light mb-4 flex items-center gap-2" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>
            <Phone className="w-5 h-5" style={{ color: '#D4AF37' }} /> {t('phoneSection')}
          </h2>
          <div className="rounded-2xl p-6 backdrop-blur-xl space-y-4" style={cardStyle}>
            <div className="flex items-center gap-2">
              {account?.phone_verified ? (
                <span className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold" style={{ background: 'rgba(34,197,94,0.15)', color: '#22C55E' }} data-testid="phone-status-verified">
                  <CheckCircle className="w-4 h-4" /> {t('phoneVerified')}
                </span>
              ) : (
                <span className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold" style={{ background: 'rgba(239,68,68,0.15)', color: '#EF4444' }} data-testid="phone-status-unverified">
                  <AlertCircle className="w-4 h-4" /> {t('phoneNotVerified')}
                </span>
              )}
            </div>
            <div>
              <label className="block text-sm mb-2" style={{ color: '#A3B8AC' }}>{t('phoneNumber')}</label>
              <input type="tel" value={phone} onChange={(e) => { setPhone(e.target.value); setOtpRequested(false); }} placeholder={t('phoneNumberPlaceholder')} className="w-full rounded-lg px-4 py-3 outline-none" style={inputStyle} data-testid="phone-input" />
            </div>
            {!otpRequested ? (
              <motion.button whileTap={{ scale: 0.98 }} onClick={handleRequestOtp} disabled={phoneSubmitting} className="w-full py-3 rounded-full font-semibold disabled:opacity-50" style={goldBtn} data-testid="send-otp-btn">
                {phoneSubmitting ? t('loading') : t('sendOTP')}
              </motion.button>
            ) : (
              <form onSubmit={handleVerifyOtp} className="space-y-3" data-testid="otp-form">
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  required
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder={t('enterOTP')}
                  className="w-full rounded-lg px-4 py-3 outline-none text-center text-xl tracking-widest font-mono"
                  style={inputStyle}
                  data-testid="otp-input"
                />
                <div className="flex gap-2">
                  <button type="button" onClick={() => { setOtpRequested(false); setOtp(''); }} className="flex-1 py-3 rounded-full" style={{ background: 'rgba(255,255,255,0.05)', color: '#F8FAF9' }}>
                    {t('cancel')}
                  </button>
                  <motion.button whileTap={{ scale: 0.98 }} type="submit" disabled={phoneSubmitting} className="flex-1 py-3 rounded-full font-semibold disabled:opacity-50" style={goldBtn} data-testid="verify-otp-btn">
                    {phoneSubmitting ? t('loading') : t('verifyOTP')}
                  </motion.button>
                </div>
              </form>
            )}
            <p className="text-xs" style={{ color: '#A3B8AC', display: process.env.NODE_ENV === 'production' ? 'none' : 'block' }}>
              {lang === 'id'
                ? 'Saat ini SMS provider belum terintegrasi. Kode OTP dicatat di log backend untuk testing.'
                : 'SMS provider not yet integrated. OTP code is logged on the backend for testing.'}
            </p>
          </div>
        </section>

        {/* Language */}
        <section data-testid="section-language">
          <h2 className="text-xl font-light mb-4 flex items-center gap-2" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>
            <Globe className="w-5 h-5" style={{ color: '#D4AF37' }} /> {t('languageSection')}
          </h2>
          <div className="rounded-2xl p-6 backdrop-blur-xl space-y-3" style={cardStyle}>
            {['id', 'en'].map((code) => (
              <button
                key={code}
                onClick={() => handleLanguageChange(code)}
                className="w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all"
                style={{
                  background: lang === code ? 'rgba(212,175,55,0.15)' : 'rgba(0,0,0,0.2)',
                  border: `1px solid ${lang === code ? 'rgba(212,175,55,0.5)' : 'rgba(255,255,255,0.05)'}`,
                  color: '#F8FAF9',
                }}
                data-testid={`lang-${code}-btn`}
              >
                <span className="flex items-center gap-3">
                  <span className="text-2xl">{code === 'id' ? '🇮🇩' : '🇬🇧'}</span>
                  <span>{code === 'id' ? t('indonesian') : t('english')}</span>
                </span>
                {lang === code && <CheckCircle className="w-5 h-5" style={{ color: '#D4AF37' }} />}
              </button>
            ))}
          </div>
        </section>

      </div>

      <MobileNav />
    </div>
  );
};
